import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import LanguageSwitcher from './LanguageSwitcher';
import AuthPromptModal from './AuthPromptModal';
import { 
  Home, LogOut, Menu, X, UserCircle, ChevronDown, 
  Heart, User, Newspaper, FileText, Settings, LogIn, Zap, PiggyBank
} from 'lucide-react';

const Header = ({ savedSchemeIds = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');
  const profileDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle navigation with auth check
  const handleProtectedNavigation = (path, message) => {
    setMobileMenuOpen(false);
    if (isAuthenticated) {
      navigate(path);
    } else {
      setAuthModalMessage(message || t('loginToAccess') || 'Please login to access this feature');
      setShowAuthModal(true);
    }
  };

  // Check active page
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navButtonClass = (path) => {
    const base = "flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors leading-relaxed";
    if (isActive(path)) {
      return `${base} bg-green-100 text-green-700 border border-green-200`;
    }
    return `${base} text-gray-600 hover:bg-gray-100`;
  };

  // Mobile bottom nav item class
  const mobileNavClass = (path) => {
    const base = "flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-colors";
    if (isActive(path)) {
      return `${base} text-green-600`;
    }
    return `${base} text-gray-500`;
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-[4rem] py-2">
            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/images/kms-logo.png" 
                alt="Know My Schemes" 
                className="w-10 h-10 rounded-xl shadow-md object-contain bg-white" 
              />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent">
                  {t('appName')}
                </h1>
                <p className="text-xs text-gray-500">{t('tagline')}</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              {/* Home */}
              <button
                onClick={() => navigate('/')}
                className={navButtonClass('/')}
              >
                <Home className="w-4 h-4" />
                <span>{t('home')}</span>
              </button>

              {/* News - Public */}
              <button
                onClick={() => navigate('/news')}
                className={navButtonClass('/news')}
                data-testid="nav-news"
              >
                <Newspaper className="w-4 h-4" />
                <span>{t('news')}</span>
              </button>

              {/* Explore Schemes - Public */}
              <button
                onClick={() => navigate('/schemes')}
                className={navButtonClass('/schemes')}
                data-testid="nav-schemes"
              >
                <FileText className="w-4 h-4" />
                <span>{t('exploreSchemes')}</span>
              </button>

              {/* Impact Analyser - Public */}
              <button
                onClick={() => navigate('/impact-analyser')}
                className={navButtonClass('/impact-analyser')}
                data-testid="nav-impact-analyser"
              >
                <Zap className="w-4 h-4" />
                <span>{t('navImpact')}</span>
              </button>

              {/* Money Hub - Public */}
              <button
                onClick={() => navigate('/money-hub')}
                className={navButtonClass('/money-hub')}
                data-testid="nav-money-hub"
              >
                <PiggyBank className="w-4 h-4" />
                <span>For You</span>
              </button>

              {/* Admin (if admin) */}
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className={navButtonClass('/admin')}
                >
                  <Settings className="w-4 h-4" />
                  <span>Admin</span>
                </button>
              )}
            </nav>

            {/* Right Side - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <LanguageSwitcher />
              
              {/* Show Login button for guests, Profile dropdown for logged-in users */}
              {isAuthenticated && user ? (
                <div className="relative" ref={profileDropdownRef}>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  >
                    <UserCircle className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">
                      {user.name}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.phone}</p>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/profile'); setProfileDropdownOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <User className="w-4 h-4 text-gray-500" />
                          {t('profile')}
                        </button>
                        <button
                          onClick={() => { navigate('/saved-schemes'); setProfileDropdownOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Heart className="w-4 h-4 text-pink-500" />
                          {t('saved')}
                          {savedSchemeIds.length > 0 && (
                            <Badge className="ml-auto bg-pink-100 text-pink-600 text-xs">
                              {savedSchemeIds.length}
                            </Badge>
                          )}
                        </button>
                      </div>
                      
                      {/* Logout */}
                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={() => { handleLogout(); setProfileDropdownOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  data-testid="header-login-btn"
                >
                  <LogIn className="w-4 h-4" />
                  {t('login') || 'Login'}
                </Button>
              )}
            </div>

            {/* Mobile - Right side with login (guests), language and menu */}
            <div className="flex md:hidden items-center gap-2">
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/login')}
                  className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg"
                  data-testid="mobile-header-login-btn"
                >
                  <LogIn className="w-4 h-4" />
                  {t('login') || 'Login'}
                </button>
              )}
              <LanguageSwitcher variant="compact" />
              <button 
                className="p-2 rounded-lg hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Menu - Profile, Admin & Logout only */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-100 space-y-2">
              {/* User Info (only if logged in) */}
              {isAuthenticated && user ? (
                <>
                  <div className="px-2 py-3 mb-2 bg-gray-50 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.phone}</p>
                  </div>

                  {/* Profile */}
                  <button
                    onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${isActive('/profile') ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <User className="w-5 h-5" />
                    {t('profile')}
                  </button>

                  {/* Money Hub */}
                  <button
                    onClick={() => { navigate('/money-hub'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${isActive('/money-hub') ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="mobile-menu-money-hub"
                  >
                    <PiggyBank className="w-5 h-5" />
                    For You
                  </button>

                  {/* Saved schemes */}
                  <button
                    onClick={() => { navigate('/saved-schemes'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${isActive('/saved-schemes') ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="mobile-menu-saved"
                  >
                    <Heart className="w-5 h-5" />
                    {t('saved') || 'Saved'}
                    {savedSchemeIds.length > 0 && (
                      <span className="ml-auto w-5 h-5 bg-pink-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {savedSchemeIds.length > 9 ? '9+' : savedSchemeIds.length}
                      </span>
                    )}
                  </button>

                  {/* Admin (if admin) */}
                  {isAdmin && (
                    <button
                      onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${isActive('/admin') ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <Settings className="w-5 h-5" />
                      {t('adminDashboard')}
                    </button>
                  )}

                  {/* Logout */}
                  <div className="pt-2 border-t border-gray-100">
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('logout')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Guest user - show Login/Signup options */}
                  <div className="px-2 py-3 mb-2 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
                    <p className="text-sm font-semibold text-gray-900">{t('welcome') || 'Welcome!'}</p>
                    <p className="text-xs text-gray-500">{t('loginToAccessAll') || 'Login to access all features'}</p>
                  </div>
                  
                  <button
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-green-600 text-white hover:bg-green-700"
                  >
                    <LogIn className="w-5 h-5" />
                    {t('login') || 'Login'}
                  </button>
                  
                  <button
                    onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-green-600 text-green-600 hover:bg-green-50"
                  >
                    <User className="w-5 h-5" />
                    {t('signup') || 'Create Account'}
                  </button>

                  {/* Money Hub - public */}
                  <button
                    onClick={() => { navigate('/money-hub'); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${isActive('/money-hub') ? 'bg-green-100 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="mobile-menu-money-hub-guest"
                  >
                    <PiggyBank className="w-5 h-5" />
                    For You
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar - Always visible on mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="flex justify-around items-center px-2 py-1 safe-area-inset-bottom">
          {/* Home */}
          <button
            onClick={() => navigate('/')}
            className={mobileNavClass('/')}
            data-testid="mobile-nav-home"
          >
            <Home className={`w-6 h-6 ${isActive('/') ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`text-xs mt-0.5 font-medium ${isActive('/') ? 'text-green-600' : 'text-gray-500'}`}>
              {t('home')}
            </span>
          </button>

          {/* News - Public */}
          <button
            onClick={() => navigate('/news')}
            className={mobileNavClass('/news')}
            data-testid="mobile-nav-news"
          >
            <Newspaper className={`w-6 h-6 ${isActive('/news') ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`text-xs mt-0.5 font-medium ${isActive('/news') ? 'text-green-600' : 'text-gray-500'}`}>
              {t('news')}
            </span>
          </button>

          {/* Schemes - Public */}
          <button
            onClick={() => navigate('/schemes')}
            className={mobileNavClass('/schemes')}
            data-testid="mobile-nav-schemes"
          >
            <FileText className={`w-6 h-6 ${isActive('/schemes') ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`text-xs mt-0.5 font-medium ${isActive('/schemes') ? 'text-green-600' : 'text-gray-500'}`}>
              {t('schemes')}
            </span>
          </button>

          {/* Impact - Public */}
          <button
            onClick={() => navigate('/impact-analyser')}
            className={mobileNavClass('/impact-analyser')}
            data-testid="mobile-nav-impact"
          >
            <Zap className={`w-6 h-6 ${isActive('/impact-analyser') ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`text-xs mt-0.5 font-medium ${isActive('/impact-analyser') ? 'text-green-600' : 'text-gray-500'}`}>
              {t('navImpact')}
            </span>
          </button>

          {/* For You (money-hub) — beside KMS Smart */}
          <button
            onClick={() => navigate('/money-hub')}
            className={mobileNavClass('/money-hub')}
            data-testid="mobile-nav-foryou"
          >
            <PiggyBank className={`w-6 h-6 ${isActive('/money-hub') ? 'text-green-600' : 'text-gray-500'}`} />
            <span className={`text-xs mt-0.5 font-medium ${isActive('/money-hub') ? 'text-green-600' : 'text-gray-500'}`}>
              For You
            </span>
          </button>
        </div>
      </nav>

      {/* Auth Prompt Modal */}
      <AuthPromptModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        message={authModalMessage}
      />
    </>
  );
};

export default Header;
